import React from 'react';
import { AlertCircle, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import type { SaveState } from '../rlhfShared/useJudgementAutosave';
import InstrumentControls, { hasRequiredRemarks, ResponseValue } from './InstrumentControls';
import { Instrument, ItemSummary } from './api';

export const MAX_REMARKS_LENGTH = 5000;

interface Props {
  instrument: Instrument;
  items: ItemSummary[];
  value: ResponseValue;
  onChange: (next: ResponseValue) => void;
  remarks: string;
  onRemarksChange: (value: string) => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  isLast: boolean;
  /** Whether the page will accept a move away from this unit. Decided by the page, not here. */
  canAdvance: boolean;
  /**
   * Whether an answer may be given at all — false when the rater cannot see what they are judging
   * (the body failed to load, its kind has no renderer) or the unit is no longer theirs.
   */
  canAnswer: boolean;
  busy: boolean;
  saveState: SaveState;
  error: string | null;
}

function SaveIndicator({ saveState, error }: { saveState: SaveState; error: string | null }) {
  if (saveState === 'saving') {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Saving…
      </span>
    );
  }
  if (saveState === 'saved') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-blue-700">
        <Check className="w-3 h-3" aria-hidden="true" /> Saved
      </span>
    );
  }
  if (saveState === 'error') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-red-600" role="alert">
        <AlertCircle className="w-3 h-3" aria-hidden="true" /> {error ?? 'Not saved'}
      </span>
    );
  }
  return null;
}

/**
 * The sticky bar every study shares: its own instrument's controls, an optional explanation, and
 * Prev/Next. Identical interaction to /rate — autosave as you type, advance with Next — because a
 * rater working several studies should not have to learn a second set of habits.
 */
const LabellingBar: React.FC<Props> = ({
  instrument,
  items,
  value,
  onChange,
  remarks,
  onRemarksChange,
  onPrev,
  onNext,
  canGoPrev,
  isLast,
  canAdvance,
  canAnswer,
  busy,
  saveState,
  error,
}) => {
  const remarksRequired = instrument.remarks === 'required';
  // Only for the tooltip. Whether Next is allowed is `canAdvance`, decided by the page, so the
  // rule that gates the button and the rule that gates autosave cannot drift apart.
  const missingRemarks = !hasRequiredRemarks(instrument, remarks);
  const locked = busy || !canAnswer;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/97 backdrop-blur border-t border-gray-200 shadow-[0_-6px_22px_rgba(15,30,28,0.07)]">
      <div className="max-w-[1140px] mx-auto px-4 md:px-8 py-3 flex flex-col md:flex-row md:items-start gap-4 md:gap-5">
        <fieldset className="shrink-0">
          <legend className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Your rating
          </legend>
          <InstrumentControls
            instrument={instrument}
            items={items}
            value={value}
            onChange={onChange}
            disabled={locked}
          />
        </fieldset>

        <div className="flex-1 min-w-0">
          <label
            htmlFor="labelling-remarks"
            className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5"
          >
            Why this answer?{' '}
            <span className="font-normal normal-case tracking-normal">
              {remarksRequired ? <span className="text-red-600">required</span> : '(optional)'}
            </span>
          </label>
          <textarea
            id="labelling-remarks"
            value={remarks}
            onChange={(e) => onRemarksChange(e.target.value)}
            disabled={locked}
            maxLength={MAX_REMARKS_LENGTH}
            placeholder="What worked, what didn't, what you'd change…"
            rows={2}
            className="w-full min-h-[42px] rounded-xl border-[1.5px] border-gray-200 px-3 py-2 text-[13px] resize-y focus:outline-none focus:border-blue-600 disabled:opacity-60"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Autosaves as you type · saved automatically when you press Next
          </p>
        </div>

        <div className="flex items-center gap-2 md:ml-auto shrink-0 flex-wrap">
          <SaveIndicator saveState={saveState} error={error} />
          <button
            type="button"
            onClick={onPrev}
            disabled={!canGoPrev || busy}
            className="px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] border-gray-200 bg-white text-[13px] font-semibold disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={busy || !canAdvance}
            title={
              canAdvance
                ? undefined
                : !canAnswer
                  ? 'This item cannot be scored right now'
                  : missingRemarks
                    ? 'This study asks you to explain your answer'
                    : 'Answer first'
            }
            className="px-4 py-2 min-h-[44px] rounded-xl bg-gray-900 text-white text-[13px] font-semibold hover:bg-gray-800 disabled:opacity-40 flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
            {isLast ? 'Finish' : 'Next'}
            {!isLast && <ChevronRight className="w-4 h-4" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabellingBar;
